import { useEffect, useState } from "react";
import api, { apiError, formatEuro } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Receipt } from "@phosphor-icons/react";

export default function Vendite() {
  const [dishes, setDishes] = useState([]);
  const [combos, setCombos] = useState([]);
  const [sales, setSales] = useState([]);
  const [form, setForm] = useState({ item_type: "dish", item_id: "", quantita: "1" });
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/dishes").then((r) => setDishes(r.data.filter((d) => d.attivo))).catch(() => {});
    api.get("/combos").then((r) => setCombos(r.data.filter((c) => c.attivo))).catch(() => {});
    api.get("/sales?limit=100").then((r) => setSales(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const items = form.item_type === "dish" ? dishes : combos;
  const selected = items.find((i) => i.id === form.item_id);
  const prezzoUnit = selected ? (form.item_type === "dish" ? selected.prezzo : selected.prezzo_combo) : 0;
  const qty = parseInt(form.quantita) || 0;

  const registra = async () => {
    if (!form.item_id) { toast.error("Seleziona un piatto o una combo"); return; }
    if (qty <= 0) { toast.error("Quantità non valida"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/sales", { item_type: form.item_type, item_id: form.item_id, quantita: qty });
      toast.success(`Vendita registrata: ${data.nome} x${data.quantita} — ${formatEuro(data.totale)}. Scorte scalate automaticamente.`);
      setForm((f) => ({ ...f, item_id: "", quantita: "1" }));
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6 fade-in" data-testid="vendite-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-extrabold">Vendite</h1>
        <p className="text-[#6B7280] mt-1">Registra una vendita: le scorte degli ingredienti vengono scalate automaticamente.</p>
      </div>

      <div className="bg-white border border-[#E5E5E0] rounded-2xl p-6" data-testid="sale-form">
        <div className="flex items-center gap-2 mb-5">
          <Receipt size={22} weight="duotone" className="text-[#D95D39]" />
          <h2 className="text-lg font-bold">Registra vendita</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
          <div className="space-y-1.5">
            <Label>Tipo</Label>
            <Select value={form.item_type} onValueChange={(v) => setForm((f) => ({ ...f, item_type: v, item_id: "" }))}>
              <SelectTrigger data-testid="sale-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="dish">Piatto</SelectItem>
                <SelectItem value="combo">Combo</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>{form.item_type === "dish" ? "Piatto" : "Combo"}</Label>
            <Select value={form.item_id} onValueChange={(v) => setForm((f) => ({ ...f, item_id: v }))}>
              <SelectTrigger data-testid="sale-item-select"><SelectValue placeholder="Seleziona..." /></SelectTrigger>
              <SelectContent>
                {items.map((i) => (
                  <SelectItem key={i.id} value={i.id}>
                    {i.nome} — {formatEuro(form.item_type === "dish" ? i.prezzo : i.prezzo_combo)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Quantità</Label>
            <Input type="number" min="1" value={form.quantita} onChange={(e) => setForm((f) => ({ ...f, quantita: e.target.value }))} data-testid="sale-quantity-input" />
          </div>
          <Button onClick={registra} disabled={saving} data-testid="sale-register-button" className="rounded-full bg-[#D95D39] hover:bg-[#BF4F2F] h-10">
            {saving ? "Registrazione..." : `Registra ${prezzoUnit && qty > 0 ? formatEuro(prezzoUnit * qty) : ""}`}
          </Button>
        </div>
      </div>

      <div className="bg-white border border-[#E5E5E0] rounded-2xl overflow-hidden">
        <Table data-testid="sales-table">
          <TableHeader className="bg-[#FAF9F6]">
            <TableRow>
              <TableHead>Data</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead>Articolo</TableHead>
              <TableHead>Quantità</TableHead>
              <TableHead>Totale</TableHead>
              <TableHead>Utente</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sales.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center text-[#6B7280] py-8">Nessuna vendita registrata.</TableCell></TableRow>
            )}
            {sales.map((s) => (
              <TableRow key={s.id} className="hover:bg-stone-50" data-testid={`sale-row-${s.id}`}>
                <TableCell className="text-sm">{new Date(s.created_at).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" })}</TableCell>
                <TableCell>
                  <Badge variant="outline" className="rounded-full">{s.item_type === "dish" ? "Piatto" : "Combo"}</Badge>
                </TableCell>
                <TableCell className="font-medium">{s.nome}</TableCell>
                <TableCell>x{s.quantita}</TableCell>
                <TableCell className="font-semibold">{formatEuro(s.totale)}</TableCell>
                <TableCell className="text-sm text-[#6B7280]">{s.utente}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
